import React, { useState, useRef, useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import { FormControlLabel, Switch } from "@mui/material";
import SunEditor from "suneditor-react";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import "suneditor/dist/css/suneditor.min.css";
import makeAnimated from "react-select/animated";
import styles from "../../Assets/css/codingform.module.css";
import Joi from "joi";
import { useQuery } from "react-query";
import { developAxios } from "../../axios.config";

// import { developAxios } from "../../axios.config";

const language_options: Array<{
	value: language_options;
	label: language_options;
}> = [
	{ value: "C", label: "C" },
	{ value: "C++", label: "C++" },
	{ value: "Java", label: "Java" },
	{ value: "Python3", label: "Python3" },
];

const difficulty_options = [
	{ value: 1, label: "1" },
	{ value: 2, label: "2" },
	{ value: 3, label: "3" },
	{ value: 4, label: "4" },
	{ value: 5, label: "5" },
];

const animatedComponents = makeAnimated();

interface ICCStep1Props {
	data: ICodingData;
	next: (val: ICodingData) => unknown;
}

type solutions_type = Array<{
	language: language_options;
	codeSolutions: Array<string>;
}>;

function RedMark() {
	return (
		<span
			style={{
				color: "red",
			}}
		>
			*
		</span>
	);
}

type testCase = Array<
	[
		{
			language: language_options_l;
		},
		Array<{
			code?: string;
			scannerInput?: string;
			name: string;
			output: string;
		}>
	]
>;

type privateTestCase = Array<
	[
		{
			language: language_options_l;
		},
		Array<{
			code?: string;
			scannerInput?: string;
			name: string;
			output: string;
			marks: number;
		}>
	]
>;

export default function CCStep1(props: ICCStep1Props) {
	const { next, data } = props;

	const [testcases, setTestcases] = useState<testCase>([]);
	const [hasScannerInputs, setHasScannerInputs] = useState<boolean>(false);
	const [hasPrivateTestCases, setHasPrivateTestCases] =
		useState<boolean>(true);
	const [hidden, setHidden] = useState<boolean>(false);
	const [description, setDescription] = useState<string>("");
	const [questionCode, setQuestionCode] = useState<
		Array<{
			language: language_options_l;
			code: string;
		}>
	>([]);
	const [privateTestcases, setPrivateTestcases] = useState<privateTestCase>(
		[]
	);
	const [languages, setLanguages] = useState<
		ReadonlyArray<{
			value: language_options;
			label: language_options;
		}>
	>([]);

	const [topics, setTopics] = useState<
		ReadonlyArray<{
			value: string;
			label: string;
		}>
	>([]);
	const [outOfScore, setOutOfScore] = useState<number>(data.outOfScore);
	const [difficultyLevel, setDifficultyLevel] = useState<{
		value: number;
		label: string;
	} | null>();
	const [solutionsCode, setSolutionsCode] = useState<solutions_type>([]);
	const name = useRef<HTMLInputElement>(null);
	const sample_input = useRef<HTMLTextAreaElement>(null);
	const sample_output = useRef<HTMLTextAreaElement>(null);
	const history = useHistory();

	const { data: topic_tags } = useQuery({
		queryKey: ["admin", "dashboard", "topics"],
		queryFn: () =>
			developAxios(localStorage.getItem("token") || "")
				.get<{
					data: {
						_id: string;
						name: string;
					}[];
				}>("/admin/dashboard/topics")
				.then(({ data }) =>
					data.data.map((val) => ({
						value: val._id,
						label: val.name,
					}))
				),
	});

	const filtered_topics = useMemo<
		Array<{
			value: string;
			label: string;
		}>
	>(() => {
		if (!topic_tags) return [];
		const topics_arr: Array<{
			value: string;
			label: string;
		}> = [];
		for (let i = 0; i < data.topics.length; i++) {
			const topic_obj = topic_tags?.filter(
				(val) => val.value === data.topics[i]
			)[0];

			if (topic_obj) topics_arr.push(topic_obj);
		}

		return topics_arr;
	}, [data, topic_tags]);

	useEffect(() => {
		setTopics(filtered_topics);
	}, [filtered_topics]);

	useEffect(() => {
		let token = localStorage.getItem("token");
		if (!token) {
			history.push(`/signin`);
			return;
		}
	}, [history]);

	useEffect(() => {
		setLanguages(
			language_options.filter((val) => data.languages.includes(val.value))
		);
		setDifficultyLevel(
			difficulty_options.filter(
				(val) => val.value === data.questionWeigh
			)[0]
		);
		setDescription(data.description);
		setOutOfScore(data.outOfScore);
		setSolutionsCode(data.solutions);
		setHidden(data.hidden);
		setHasScannerInputs(data.hasScannerInputs);
		setTestcases(data.testcases);
		setPrivateTestcases(data.privateTestcases || []);
		setQuestionCode(data.questionCode);
		setHasPrivateTestCases(data.hasPrivateTestCases);
	}, [data]);

	useEffect(() => {
		function checkIfTestCaseExists(
			lang: language_options_l,
			type: "public" | "private"
		) {
			if (type === "public") {
				return (
					testcases.filter((val) => val[0].language === lang).length >
					0
				);
			} else {
				return (
					privateTestcases.filter((val) => val[0].language === lang)
						.length > 0
				);
			}
		}

		function checkIfSolutionExists(
			lang: language_options,
			type: "solution" | "boilerplate" = "solution"
		) {
			if (type === "solution") {
				return (
					solutionsCode.filter((val) => val.language === lang)
						.length > 0
				);
			} else {
				return (
					questionCode.filter(
						(val) => val.language === getTestCaseLang(lang)
					).length > 0
				);
			}
		}
		let test_case: testCase = [];
		let pvt_test_case: privateTestCase = [];
		let solutions: solutions_type = [];
		let question_code: Array<{
			code: string;
			language: language_options_l;
		}> = [];
		languages.forEach((lang) => {
			if (!checkIfTestCaseExists(getTestCaseLang(lang.value), "public")) {
				test_case.push([
					{
						language: getTestCaseLang(lang.value),
					},
					[
						{
							code: getCode(lang.value),
							name: "Test Case 1",
							output: "",
						},
					],
				]);
			}
			if (
				!checkIfTestCaseExists(getTestCaseLang(lang.value), "private")
			) {
				pvt_test_case.push([
					{
						language: getTestCaseLang(lang.value),
					},
					[
						{
							code: getCode(lang.value),
							name: "Test Case 1",
							output: "",
							marks: 1,
						},
					],
				]);
			}

			if (!checkIfSolutionExists(lang.value)) {
				solutions.push({
					language: lang.value,
					codeSolutions: [""],
				});
			}

			if (!checkIfSolutionExists(lang.value, "boilerplate")) {
				question_code.push({
					code: getCode(lang.value),
					language: getTestCaseLang(lang.value),
				});
			}
		});

		setSolutionsCode((prev) => [...prev, ...solutions]);
		setTestcases((prev) => [...prev, ...test_case]);
		setPrivateTestcases((prev) => [...prev, ...pvt_test_case]);
		setQuestionCode((prev) => [...prev, ...question_code]);
	}, [languages]);

	function getCode(lang: language_options): string {
		switch (lang) {
			case "C":
				return `int main(){\n    // write your code here\n}`;

			case "C++":
				return `int main(){\n    // write your code here\n}`;

			case "Python3":
				return "if __name__=='__main__':\n    ## write your code here";

			case "Java":
				return "public class Main {\n\tpublic static void main(String[] args){\n\t}\n}";

			default:
				return "";
		}
	}

	function getTestCaseLang(val: language_options): language_options_l {
		switch (val) {
			case "C":
				return "c";
			case "C++":
				return "c++";

			case "Java":
				return "java";

			default:
				return "python3";
		}
	}

	function handleCheck(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();

		let schema = Joi.object<{
			languages: Array<language_options>;
			description: string;
			name: string;
			sampleInput: string;
			sampleOutput: string;
			courseId: string;
			questionWeigh: number;
		}>({
			languages: Joi.array()
				.items("C", "C++", "Java", "Python3")
				.min(1)
				.messages({
					"array.min": "Atleast select 1 language to proceed",
				}),
			description: Joi.string().required().messages({
				"any.required": "Description is a required field",
			}),
			name: Joi.string().required().messages({
				"any.required": "Name is a required field",
			}),
			sampleInput: Joi.string().required().messages({
				"any.required": "Sample Input is a required field",
			}),
			sampleOutput: Joi.string().required().messages({
				"any.required": "Sample Output is a required field",
			}),
			questionWeigh: Joi.number().required().messages({
				"any.required": "Difficulty Level is a required field",
			}),
		});

		let { value, error } = schema.validate({
			languages: languages.map((val) => val.value),
			description,
			name: name.current?.value,
			sampleInput: sample_input.current?.value,
			sampleOutput: sample_output.current?.value,
			questionWeigh: difficultyLevel?.value,
		});

		if (error) {
			alert(error.message);
			return;
		}
		if (!value) {
			alert("Error in uploading the data");
			return;
		}
		let page_data: ICodingData = {
			...data,
			...value,
			hasScannerInputs,
			hidden,
			solutions: solutionsCode,
			testcases,
			privateTestcases,
			questionCode: questionCode,
			outOfScore: outOfScore,
			hasPrivateTestCases,
			topics: topics.map((val) => val.value),
		};
		next(page_data);
	}

	return (
		<div className="d-flex justify-content-center align-items-center my-4">
			<Form className={`${styles.step_one_form}`} onSubmit={handleCheck}>
				<Form.Group>
					<Form.Label>
						Name
						<RedMark /> :
					</Form.Label>
					<Form.Control
						type="text"
						placeholder="coding question tile"
						defaultValue={data.name}
						ref={name}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>
						Description
						<RedMark /> :
					</Form.Label>
					<SunEditor
						setContents={description}
						onChange={(e) => setDescription(e)}
						setOptions={{
							buttonList: [
								[
									"undo",
									"redo",
									"font",
									"fontSize",
									"formatBlock",
									"paragraphStyle",
									"blockquote",
									"bold",
									"underline",
									"italic",
									"strike",
									"subscript",
									"superscript",
									"fontColor",
									"hiliteColor",
									"textStyle",
									"removeFormat",
									"outdent",
									"indent",
									"align",
									"horizontalRule",
									"list",
									"lineHeight",
									"table",
									"link",
									"image",
									"video",
									"fullScreen",
									"showBlocks",
									"codeView",
								],
							],
						}}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>
						Sample Input
						<RedMark /> :
					</Form.Label>
					<Form.Control
						as="textarea"
						placeholder="sample input code"
						ref={sample_input}
						defaultValue={data.sampleInput}
					/>
				</Form.Group>

				<Form.Group>
					<Form.Label>
						Sample Output
						<RedMark /> :
					</Form.Label>
					<Form.Control
						as="textarea"
						placeholder="sample output code"
						ref={sample_output}
						defaultValue={data.sampleOutput}
					/>
				</Form.Group>
				<Form.Group>
					<Form.Label>
						Select Languages
						<RedMark /> :
					</Form.Label>
					<Select
						options={language_options}
						closeMenuOnSelect={false}
						components={animatedComponents}
						isMulti={true}
						value={languages}
						onChange={(val) => {
							setLanguages(val);
						}}
					/>
				</Form.Group>

				{topic_tags && (
					<Form.Group>
						<Form.Label>
							Select Topics
							<RedMark /> :
						</Form.Label>
						<Select
							options={topic_tags}
							closeMenuOnSelect={false}
							components={animatedComponents}
							isMulti={true}
							value={topics}
							onChange={(val) => {
								setTopics(val);
							}}
						/>
					</Form.Group>
				)}

				<Form.Group>
					<Form.Label>Select Difficulty Level :</Form.Label>
					<Select
						options={difficulty_options}
						closeMenuOnSelect={true}
						value={difficultyLevel}
						onChange={(e) => setDifficultyLevel(e)}
					/>
				</Form.Group>
				<Form.Group>
					<Form.Label>
						Out of Score:
						<RedMark /> :
					</Form.Label>
					<Form.Control
						type="number"
						placeholder="Out of Score"
						defaultValue={outOfScore}
						onChange={(e) => {
							setOutOfScore(parseInt(e.target.value, 10));
							// alert(outOfScore)
						}}
					/>
				</Form.Group>
				<Form.Group>
					<FormControlLabel
						control={
							<Switch
								checked={hasScannerInputs}
								onChange={() =>
									setHasScannerInputs((prev) => !prev)
								}
							/>
						}
						label="Has Scanner Inputs"
						labelPlacement="start"
					/>
				</Form.Group>
				<Form.Group>
					<FormControlLabel
						control={
							<Switch
								checked={hidden}
								onChange={() => setHidden((prev) => !prev)}
							/>
						}
						label="Hidden"
						labelPlacement="start"
					/>
				</Form.Group>
				<Form.Group>
					<FormControlLabel
						control={
							<Switch
								checked={hasPrivateTestCases}
								onChange={() =>
									setHasPrivateTestCases((prev) => !prev)
								}
							/>
						}
						label="Show Private Test Cases"
						labelPlacement="start"
					/>
				</Form.Group>
				<button
					className="my-2 px-3 py-1 rounded hover:bg-blue-700"
					style={{
						backgroundColor: "blue",
						color: "#fff",
					}}
					type="submit"
				>
					Next
				</button>
			</Form>
		</div>
	);
}
